import React, { useState, useEffect } from 'react';
import { useOAuth } from '../hooks/useOAuth';
import { useAuth } from '../contexts/AuthContext';
import configService from '../services/configService';

const services = {
  google_calendar: {
    name: 'Google Calendar',
    endpoint: '/oauth/google_calendar/auth',
    scopes: ['calendar']
  },
  calendly: {
    name: 'Calendly',
    endpoint: '/oauth/calendly/auth',
    scopes: ['default']
  },
  acuity: {
    name: 'Acuity Scheduling',
    endpoint: '/oauth/acuity/auth',
    scopes: ['api-v1']
  }
};

const ConnectServicesStep = () => {
  const { connectService, loading } = useOAuth();
  const { logout, user } = useAuth();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [connections, setConnections] = useState({});
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [calendarChoice, setCalendarChoice] = useState('bot'); // 'bot' or 'personal'
  const [sharingCalendar, setSharingCalendar] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  
  // Get assistant phone number for email display
  const [assistantPhone, setAssistantPhone] = useState(null);
  const assistantEmail = assistantPhone ? `assistant${assistantPhone}@remote-assistant.ai` : 'assistant+[phone-number]@remote-assistant.ai';
  
  // Load assistant phone number from config
  useEffect(() => {
    const loadAssistantPhone = async () => {
      try {
        const cognitoUserId = configService.getUserId();
        if (cognitoUserId) {
          const config = await configService.getBusinessConfigByUserId(cognitoUserId);
          if (config?.assistant_phone) {
            setAssistantPhone(config.assistant_phone);
          }
        }
      } catch (error) {
        console.warn('Could not load assistant phone:', error);
      }
    };
    loadAssistantPhone();
  }, []);
  
  const handleShareCalendar = async () => {
    console.log('📅 SHARE CALENDAR: Starting calendar sharing process');
    setSharingCalendar(true);
    setShareMessage('');
    
    try {
      const userEmail = user?.attributes?.email;
      const userId = user?.attributes?.sub;
      
      console.log('📅 SHARE CALENDAR: User data:', {
        userEmail,
        userId,
        assistantPhone
      });
      
      if (!userEmail) {
        console.error('📅 SHARE CALENDAR: User email not found');
        setShareMessage('User email not found');
        return;
      }
      
      if (!assistantPhone) {
        console.error('📅 SHARE CALENDAR: Assistant phone number not configured');
        setShareMessage('Assistant phone number not configured');
        return;
      }
      
      // First create assistant calendar if it doesn't exist
      const createUrl = `https://oauth.remote-assistant.ai/oauth/users/${userId}/create-calendar`;
      const createPayload = {
        assistant_phone: assistantPhone,
        business_name: 'Business Calendar'
      };
      
      console.log('📅 SHARE CALENDAR: Creating calendar with:', {
        url: createUrl,
        payload: createPayload
      });
      
      const createResponse = await fetch(createUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await configService.getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createPayload)
      });
      
      console.log('📅 SHARE CALENDAR: Create calendar response status:', createResponse.status);
      console.log('📅 SHARE CALENDAR: Create calendar response headers:', [...createResponse.headers.entries()]);
      
      const createResponseText = await createResponse.text();
      console.log('📅 SHARE CALENDAR: Create calendar raw response:', createResponseText.substring(0, 200));
      
      let createResult;
      try {
        createResult = JSON.parse(createResponseText);
        console.log('📅 SHARE CALENDAR: Create calendar parsed result:', createResult);
      } catch (parseError) {
        console.error('📅 SHARE CALENDAR: Failed to parse create calendar response as JSON:', parseError);
        console.log('📅 SHARE CALENDAR: Is HTML response?', createResponseText.startsWith('<!doctype') || createResponseText.startsWith('<!DOCTYPE'));
        throw new Error('Create calendar API returned invalid JSON: ' + createResponseText.substring(0, 100));
      }
      
      // Then share the calendar
      const shareUrl = `https://oauth.remote-assistant.ai/oauth/users/${userId}/share-calendar`;
      const sharePayload = {
        user_email: userEmail,
        assistant_phone: assistantPhone
      };
      
      console.log('📅 SHARE CALENDAR: Sharing calendar with:', {
        url: shareUrl,
        payload: sharePayload
      });
      
      const shareResponse = await fetch(shareUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await configService.getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sharePayload)
      });
      
      console.log('📅 SHARE CALENDAR: Share calendar response status:', shareResponse.status);
      console.log('📅 SHARE CALENDAR: Share calendar response headers:', [...shareResponse.headers.entries()]);
      
      const shareResponseText = await shareResponse.text();
      console.log('📅 SHARE CALENDAR: Share calendar raw response:', shareResponseText.substring(0, 200));
      
      let shareResult;
      try {
        shareResult = JSON.parse(shareResponseText);
        console.log('📅 SHARE CALENDAR: Share calendar parsed result:', shareResult);
      } catch (parseError) {
        console.error('📅 SHARE CALENDAR: Failed to parse share calendar response as JSON:', parseError);
        console.log('📅 SHARE CALENDAR: Is HTML response?', shareResponseText.startsWith('<!doctype') || shareResponseText.startsWith('<!DOCTYPE'));
        throw new Error('Share calendar API returned invalid JSON: ' + shareResponseText.substring(0, 100));
      }
      
      if (shareResult.success) {
        console.log('📅 SHARE CALENDAR: Calendar shared successfully');
        setShareMessage('Calendar shared successfully! Check your Google Calendar.');
      } else {
        console.error('📅 SHARE CALENDAR: Share failed:', shareResult.message);
        setShareMessage(shareResult.message || 'Failed to share calendar');
      }
    } catch (error) {
      console.error('📅 SHARE CALENDAR: Exception occurred:', error);
      setShareMessage('Error sharing calendar: ' + error.message);
    } finally {
      setSharingCalendar(false);
      console.log('📅 SHARE CALENDAR: Process completed');
    }
  };

  useEffect(() => {
    loadConnectionStatus();
  }, []);

  const loadConnectionStatus = async () => {
    try {
      const connectionsData = await configService.getOAuthConnections();
      const connectionMap = {};
      connectionsData.connections.forEach(conn => {
        connectionMap[conn.service] = conn;
      });
      setConnections(connectionMap);
    } catch (error) {
      console.warn('Could not load connection status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleConnect = async (service) => {
    try {
      setMessage('');
      await connectService(service);
      setMessage(`${services[service].name} connected successfully!`);
      setMessageType('success');
      // Reload connection status after successful connection
      setTimeout(() => loadConnectionStatus(), 1000);
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        logout();
        return;
      }
      setMessage(error.message || `Failed to connect ${services[service].name}`);
      setMessageType('error');
    }
  };

  const getConnectionButton = (service) => {
    const serviceConfig = services[service];
    const connection = connections[service];
    const isLoading = loading;
    
    const getButtonText = () => {
      if (isLoading) return 'Connecting...';
      if (loadingStatus) return 'Loading...';
      if (connection) {
        if (connection.status === 'expired') return `Reconnect ${serviceConfig.name}`;
        return `✓ ${serviceConfig.name} Connected`;
      }
      return `Connect ${serviceConfig.name}`;
    };

    const getButtonStyle = () => {
      if (connection?.status === 'connected') {
        return {
          background: '#10b981',
          opacity: isLoading || loadingStatus ? 0.7 : 1,
          cursor: isLoading || loadingStatus ? 'not-allowed' : 'pointer'
        };
      }
      if (connection?.status === 'expired') {
        return {
          background: '#f59e0b',
          opacity: isLoading || loadingStatus ? 0.7 : 1,
          cursor: isLoading || loadingStatus ? 'not-allowed' : 'pointer'
        };
      }
      return {
        opacity: isLoading || loadingStatus ? 0.7 : 1,
        cursor: isLoading || loadingStatus ? 'not-allowed' : 'pointer'
      };
    };

    return (
      <button 
        className="btn-primary w-full" 
        onClick={() => handleConnect(service)}
        disabled={isLoading || loadingStatus}
        style={getButtonStyle()}
      >
        {getButtonText()}
      </button>
    );
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Email Configuration Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
            <span className="text-white text-sm font-bold">📧</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Assistant Email Configuration</h3>
        </div>
        <div className="space-y-3 text-sm text-gray-700">
          <p>
            <strong>Your assistant has its own dedicated email address:</strong>
          </p>
          <div className="bg-white border border-blue-300 rounded-md p-3 font-mono text-blue-700">
            {assistantEmail}
          </div>
          <p>
            This email is specifically configured for your business and will be used for email communications from your assistant.
          </p>
        </div>
      </div>

      {/* Calendar Choice Section */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center mr-3">
            <span className="text-white text-sm font-bold">📅</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Calendar Integration</h3>
        </div>
        
        <p className="text-sm text-gray-700 mb-4">
          Choose how you want your assistant to handle calendar scheduling:
        </p>
        
        {/* Calendar Choice Radio Buttons */}
        <div className="space-y-3 mb-6">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input 
              type="radio" 
              name="calendarChoice" 
              value="bot" 
              checked={calendarChoice === 'bot'}
              onChange={(e) => setCalendarChoice(e.target.value)}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900">Use Assistant's Default Calendar</div>
              <div className="text-sm text-gray-600">
                Your assistant will use its own built-in calendar system. No additional setup required.
              </div>
            </div>
          </label>
          
          <label className="flex items-start space-x-3 cursor-pointer">
            <input 
              type="radio" 
              name="calendarChoice" 
              value="personal" 
              checked={calendarChoice === 'personal'}
              onChange={(e) => setCalendarChoice(e.target.value)}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900">Connect Your Personal Calendar</div>
              <div className="text-sm text-gray-600">
                Connect your existing calendar service for synchronized scheduling.
              </div>
            </div>
          </label>
        </div>

        {/* Calendar Connection Options - Only show if personal calendar is selected */}
        {calendarChoice === 'personal' && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="font-medium text-gray-900 mb-3">Connect Your Calendar Service:</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Google Calendar</label>
                {getConnectionButton('google_calendar')}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Calendly</label>
                {getConnectionButton('calendly')}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Acuity Scheduling</label>
                {getConnectionButton('acuity')}
              </div>
            </div>
          </div>
        )}
        
        {calendarChoice === 'bot' && (
          <div className="space-y-3 mt-4">
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <div className="flex items-center">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-sm text-green-700">
                  Your assistant will use its default calendar system.
                </span>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex items-start space-x-2">
                <span className="text-blue-500 mt-0.5">🔗</span>
                <div className="flex-1">
                  <div className="text-sm font-medium text-blue-900 mb-1">
                    Share Assistant's Calendar With You
                  </div>
                  <div className="text-xs text-blue-700 mb-2">
                    Get read-only access to your assistant's calendar so you can see when appointments are scheduled.
                  </div>
                  <button 
                    onClick={handleShareCalendar}
                    disabled={sharingCalendar}
                    className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded disabled:opacity-50"
                  >
                    {sharingCalendar ? 'Sharing...' : 'Share Calendar'}
                  </button>
                  {shareMessage && (
                    <div className={`text-xs mt-1 ${
                      shareMessage.includes('success') ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {shareMessage}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {message && (
        <div style={{
          padding: '0.75rem',
          borderRadius: '4px',
          fontSize: '0.9rem',
          background: messageType === 'success' ? '#f0fdf4' : '#fef2f2',
          color: messageType === 'success' ? '#166534' : '#dc2626',
          border: `1px solid ${messageType === 'success' ? '#bbf7d0' : '#fecaca'}`
        }}>
          {message}
        </div>
      )}
    </div>
  );
};

// Keep the original CalendarConnectButton for backward compatibility
const CalendarConnectButton = ({ service = 'google_calendar' }) => {
  const { connectService, loading } = useOAuth();
  const { logout } = useAuth();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const serviceConfig = services[service];

  useEffect(() => {
    loadConnectionStatus();
  }, [service]);

  const loadConnectionStatus = async () => {
    try {
      const connections = await configService.getOAuthConnections();
      const connection = connections.connections.find(conn => conn.service === service);
      setConnectionStatus(connection);
    } catch (error) {
      console.warn('Could not load connection status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleConnect = async () => {
    try {
      setMessage('');
      await connectService(service);
      setMessage(`${serviceConfig.name} connected successfully!`);
      setMessageType('success');
      // Reload connection status after successful connection
      setTimeout(() => loadConnectionStatus(), 1000);
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        logout();
        return;
      }
      setMessage(error.message || `Failed to connect ${serviceConfig.name}`);
      setMessageType('error');
    }
  };

  const getButtonText = () => {
    if (loading) return 'Connecting...';
    if (loadingStatus) return 'Loading...';
    if (connectionStatus) {
      if (connectionStatus.status === 'expired') return `Reconnect ${serviceConfig.name}`;
      return `✓ ${serviceConfig.name} Connected`;
    }
    return `Connect ${serviceConfig.name}`;
  };

  const getButtonStyle = () => {
    if (connectionStatus?.status === 'connected') {
      return {
        background: '#10b981',
        opacity: loading || loadingStatus ? 0.7 : 1,
        cursor: loading || loadingStatus ? 'not-allowed' : 'pointer'
      };
    }
    if (connectionStatus?.status === 'expired') {
      return {
        background: '#f59e0b',
        opacity: loading || loadingStatus ? 0.7 : 1,
        cursor: loading || loadingStatus ? 'not-allowed' : 'pointer'
      };
    }
    return {
      opacity: loading || loadingStatus ? 0.7 : 1,
      cursor: loading || loadingStatus ? 'not-allowed' : 'pointer'
    };
  };

  return (
    <div>
      <button 
        className="btn-primary" 
        onClick={handleConnect}
        disabled={loading || loadingStatus}
        style={getButtonStyle()}
      >
        {getButtonText()}
      </button>
      
      {message && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          borderRadius: '4px',
          fontSize: '0.9rem',
          background: messageType === 'success' ? '#f0fdf4' : '#fef2f2',
          color: messageType === 'success' ? '#166534' : '#dc2626',
          border: `1px solid ${messageType === 'success' ? '#bbf7d0' : '#fecaca'}`
        }}>
          {message}
        </div>
      )}
    </div>
  );
};

export default CalendarConnectButton;
export { ConnectServicesStep };